import {
  Component,
  inject,
  ViewChild,
  ChangeDetectionStrategy,
} from '@angular/core';

import { Store } from '@ngrx/store';
import { TemplatePortal } from '@angular/cdk/portal';
import { TranslateService } from '@ngx-translate/core';
import { faGear, faUserPlus } from '@fortawesome/free-solid-svg-icons';

import { PortalRegistryService } from 'src/app/core/services/portal-registry.service';

import { FormEditorDialogBase } from 'src/app/shared/abstract/form-editor-dialog-base';

import { NomObjectClass } from 'src/app/core/constants';
import { UserGroup } from 'src/app/core/models/core.models';
import { getIconForDataObject } from 'src/app/shared/utils/object-utils';

import {
  resetPrincipalsState,
  clearTemporaryChanges,
} from 'src/app/core/actions/principals.actions';

import { AppState } from 'src/app/reducers';

interface DialogData {
  userGroup: UserGroup | null;
}
interface DialogOutput {}

@Component({
  selector: 'app-editor-user-group-dialog',
  templateUrl: './editor-user-group-dialog.component.html',
  styleUrl: './editor-user-group-dialog.component.scss',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
})
export class EditorUserGroupDialogComponent extends FormEditorDialogBase<
  any,
  DialogData,
  DialogOutput
> {
  @ViewChild('editorUserGroup')
  protected override editorComponent!: any;

  private store = inject(Store<AppState>);
  private translateService = inject(TranslateService);
  private portalRegistry = inject(PortalRegistryService);

  public tabsPortal!: TemplatePortal;
  public formPortal!: TemplatePortal;

  actionIcon = faGear;
  actionTitle = this.translateService.instant(
    'popup.action-title.create-new-user-group',
  );
  objectIcon = getIconForDataObject(NomObjectClass.USER_GROUP);
  objectTitle = this.translateService.instant('popup.object-title.untitled');

  get userGroup() {
    return this.dialogData.userGroup as UserGroup | null;
  }

  setupUiState(): void {
    if (this.userGroup) {
      this.actionTitle = this.translateService.instant(
        'popup.action-title.edit-user-group-settings',
      );
      this.objectTitle = this.userGroup.title;
    } else {
      this.actionIcon = faUserPlus;
    }
  }

  ngOnInit(): void {
    this.setupUiState();
  }

  ngAfterViewInit(): void {
    this.tabsPortal = this.portalRegistry.get('general-form-editor-tabs');
    this.formPortal = this.portalRegistry.get('general-form-editor-form');

    // ! Setup dirty-close protection
    this.initializeFormEditorProtection();
  }

  ngOnDestroy(): void {
    // ! Clear temporary changes before resetting principals state
    this.store.dispatch(clearTemporaryChanges());
    this.store.dispatch(resetPrincipalsState());

    this.portalRegistry.clearAll();
  }

  onTitleChange(title: string): void {}

  async cancelAction(): Promise<void> {
    await this.cancel();
  }
}
